import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { formatFileSize, getFileIconByExtension } from '@/constants/utils/files';

/**
 * FilePreviewTile — square preview tile for an uploaded file.
 *
 * Shows the file's image thumbnail when available; otherwise renders a colored
 * icon card based on the file extension. A bottom strip shows filename + size,
 * and a floating remove button appears in the top-right when an onRemove
 * handler is supplied.
 *
 * `contentFit` defaults to `cover` (good for multi-file grids). Use `contain`
 * for single-image fields so non-square uploads are not visually cropped.
 */
export default function FilePreviewTile({
  file,
  onRemove,
  onPress,
  slotLabel,
  size = 'md',
  isSample,
  contentFit = 'cover',
}) {
  const { colors } = useTheme();

  const previewUrl = file?.cdnPath || file?.thumbnailPath || file?.url || file?.uri || null;
  const isVideo = file?.mimeType?.startsWith?.('video/') || file?.fileType === 'video';
  const isAudio = file?.mimeType?.startsWith?.('audio/') || file?.fileType === 'audio';
  const isImage = !!previewUrl && !isVideo && !isAudio &&
    (typeof file?.mimeType === 'string' ? file.mimeType.startsWith('image/') : true);
  const showSampleBadge = !!(isSample || file?._placeholder);

  const ext = (file?.name || '').split('.').pop()?.toLowerCase() || '';
  const fallbackIcon = isVideo ? 'film' : isAudio ? 'music' : (getFileIconByExtension(ext) || 'file');
  const sizeBytes = file?.sizeInBytes ?? file?.sizeBytes ?? file?.size ?? null;
  const dim = size === 'sm' ? 80 : 116;

  const styles = useMemo(() => StyleSheet.create({
    tile: {
      width: dim,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      overflow: 'hidden',
      ...(Platform.OS === 'web' && { transition: 'transform 160ms ease, border-color 160ms ease' }),
    },
    tileHover: {
      borderColor: colors.text_color_primary,
      ...(Platform.OS === 'web' && { transform: [{ translateY: -2 }] }),
    },
    media: {
      width: dim,
      height: dim,
      backgroundColor: colors.solid_color_block,
      alignItems: 'center',
      justifyContent: 'center',
    },
    image: { width: dim, height: dim },
    fallback: { width: dim, height: dim, alignItems: 'center', justifyContent: 'center', gap: 4 },
    extPill: {
      paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
      backgroundColor: colors.solid_color_tertiary,
      borderWidth: 1, borderColor: colors.border_color_secondary,
    },
    meta: { paddingVertical: 6, paddingHorizontal: 8, gap: 1, minWidth: 0, overflow: 'hidden' },
    removeBtn: {
      position: 'absolute', top: 6, right: 6,
      width: 22, height: 22, borderRadius: 999,
      backgroundColor: 'rgba(0,0,0,0.65)',
      alignItems: 'center', justifyContent: 'center',
    },
    slotChip: {
      position: 'absolute', bottom: 6, left: 6,
      paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999,
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
    typeOverlay: {
      position: 'absolute', bottom: 6, right: 6,
      width: 24, height: 24, borderRadius: 999,
      backgroundColor: 'rgba(0,0,0,0.6)',
      alignItems: 'center', justifyContent: 'center',
    },
    sampleBadge: {
      position: 'absolute', top: 6, left: 6,
      flexDirection: 'row', alignItems: 'center', gap: 4,
      paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999,
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
  }), [colors, dim]);

  const Inner = () => (
    <>
      <View style={styles.media}>
        {isImage ? (
          <Image source={{ uri: previewUrl }} style={styles.image} contentFit={contentFit} />
        ) : (
          <View style={styles.fallback}>
            <Icon name={fallbackIcon} size={size === 'sm' ? 22 : 30} color={colors.text_color_secondary} />
            {ext ? (
              <View style={styles.extPill}>
                <Text size='xs' weight='bold' color='secondary'>{ext.toUpperCase()}</Text>
              </View>
            ) : null}
          </View>
        )}

        {(isVideo || isAudio) && (
          <View style={styles.typeOverlay}>
            <Icon name={isVideo ? 'play' : 'music'} size={11} color='#ffffff' />
          </View>
        )}

        {!!slotLabel && (
          <View style={styles.slotChip}>
            <Text size='xs' weight='bold' style={{ color: '#ffffff' }} numberOfLines={1}>
              {slotLabel}
            </Text>
          </View>
        )}

        {showSampleBadge && (
          <View style={styles.sampleBadge}>
            <Icon name='sparkles' size={9} color='#ffffff' />
            <Text size='xxs' weight='bold' style={{ color: '#ffffff' }}>SAMPLE</Text>
          </View>
        )}

        {!!onRemove && (
          <Pressable onPress={onRemove} style={styles.removeBtn} hitSlop={8}>
            <Icon name='xmark' size={11} color='#ffffff' />
          </Pressable>
        )}
      </View>

      <View style={styles.meta}>
        <Text
          size='xs'
          weight='bold'
          numberOfLines={2}
          ellipsizeMode="tail"
          style={Platform.OS === 'web' ? { overflowWrap: 'anywhere', wordBreak: 'break-word' } : undefined}
        >
          {file?.name || 'Untitled'}
        </Text>
        <Text size='xs' color='tertiary' numberOfLines={1}>
          {sizeBytes != null ? formatFileSize(sizeBytes) : (ext ? ext.toUpperCase() : '—')}
        </Text>
      </View>
    </>
  );

  if (onPress) {
    return (
      <Pressable onPress={onPress} style={({ hovered }) => [styles.tile, hovered && styles.tileHover]}>
        <Inner />
      </Pressable>
    );
  }

  return <View style={styles.tile}><Inner /></View>;
}

/**
 * EmptySlotTile — companion ghost tile for labeled-slot uploads. Renders a
 * dashed square with the slot label so the layout feels intentional before
 * any files are picked. Tappable: invokes `onPress` so the parent can fire
 * its picker for that specific slot.
 */
export function EmptySlotTile({ label, description, size = 'md', onPress, disabled }) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const dim = size === 'sm' ? 80 : 116;
  const styles = useMemo(() => StyleSheet.create({
    tile: {
      width: dim,
      height: dim + 36,
      borderRadius: 12,
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: colors.border_color_primary,
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 8,
      gap: 8,
    },
    tileHover: {
      borderColor: colors.text_color_primary,
      backgroundColor: colors.solid_color_block,
    },
    iconBubble: {
      width: 36, height: 36, borderRadius: 999,
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: colors.solid_color_block,
      borderWidth: 1, borderColor: colors.border_color_secondary,
    },
    label: { textAlign: 'center' },
  }), [colors, dim]);

  const Inner = (
    <>
      <View style={styles.iconBubble}>
        <Icon name='plus' size={16} color={colors.text_color_secondary} />
      </View>
      <Text size='xs' weight='bold' color='primary' numberOfLines={1} style={styles.label}>
        {label || 'Empty slot'}
      </Text>
      {description ? (
        <Text size='xs' color='tertiary' numberOfLines={2} style={styles.label}>
          {description}
        </Text>
      ) : null}
    </>
  );

  if (onPress && !disabled) {
    return (
      <Pressable
        onPress={onPress}
        onHoverIn={() => Platform.OS === 'web' && setHovered(true)}
        onHoverOut={() => Platform.OS === 'web' && setHovered(false)}
        style={[styles.tile, hovered && styles.tileHover]}
      >
        {Inner}
      </Pressable>
    );
  }

  return <View style={styles.tile}>{Inner}</View>;
}

